import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp, LifeBuoy, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useSort, type SortState } from "@/components/ui/SortTh";
import { trpc } from "@/lib/trpc";
import { CredentialsDialog } from "./CredentialsDialog";
import { ago, dt, nf, ROLE_LABEL } from "./format";
import { Btn, C, Confirm, Dot, Input, LogoPlate, Modal, Pill, Select, Switch, Textarea, useToast } from "./ui";
import { Trash } from "./Trash";

type Creds = { login: string; password: string; appUrl?: string | null } | null;

function Th({ k, sort, onSort, children, num }: { k: string; sort: SortState; onSort: (k: string) => void; children: ReactNode; num?: boolean }) {
  const active = sort?.key === k;
  return (
    <th className={`sortable${num ? " num" : ""}`} onClick={() => onSort(k)}>
      {children}
      {active && (sort?.dir === "asc" ? <ChevronUp size={11} style={{ marginLeft: 4 }} /> : <ChevronDown size={11} style={{ marginLeft: 4 }} />)}
    </th>
  );
}

export function Orgs({ selectedId, onSelect }: { selectedId: string | null; onSelect: (id: string | null) => void }) {
  const list = trpc.platform.orgs.list.useQuery();
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [creds, setCreds] = useState<{ title: string; c: Creds }>({ title: "", c: null });

  const rows = (list.data ?? []).filter((o) => !search.trim() || o.legalName.toLowerCase().includes(search.trim().toLowerCase()));
  const { sorted, sort, toggle } = useSort(rows, {
    name: (o) => o.legalName,
    status: (o) => (o.suspendedAt ? "gesperrt" : "aktiv"),
    users: (o) => o.users,
    customers: (o) => o.customers,
    invoices: (o) => o.invoices,
    articles: (o) => o.articles,
    login: (o) => o.lastLoginAt && new Date(o.lastLoginAt).getTime(),
    activity: (o) => o.lastActivityAt && new Date(o.lastActivityAt).getTime(),
    errors: (o) => o.errors7,
  });

  return (
    <div className="pf-grid" style={{ gap: 18 }}>
      <div className="pf-row" style={{ flexWrap: "wrap" }}>
        <div>
          <h2 className="pf-title">Betriebe</h2>
          <p className="pf-lead">{list.data ? `${list.data.length} Kunden auf der Plattform` : "Lädt …"}</p>
        </div>
        <div style={{ flex: 1 }} />
        <div className="pf-search">
          <Search />
          <input className="pf-input" placeholder="Betrieb suchen …" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Btn variant="primary" onClick={() => setCreating(true)}>
          <Plus size={16} /> Neuer Betrieb
        </Btn>
      </div>

      <div className="pf-card pf-table-wrap" style={{ padding: 8 }}>
        {list.isError ? (
          <div style={{ padding: 20 }}>
            Betriebe konnten nicht geladen werden. <Btn size="sm" onClick={() => list.refetch()}>Erneut versuchen</Btn>
          </div>
        ) : (
          <table className="pf-table">
            <thead>
              <tr>
                <Th k="name" sort={sort} onSort={toggle}>Betrieb</Th>
                <Th k="status" sort={sort} onSort={toggle}>Status</Th>
                <Th k="users" sort={sort} onSort={toggle} num>Benutzer</Th>
                <Th k="customers" sort={sort} onSort={toggle} num>Kunden</Th>
                <Th k="invoices" sort={sort} onSort={toggle} num>Rechnungen</Th>
                <Th k="articles" sort={sort} onSort={toggle} num>Artikel</Th>
                <Th k="login" sort={sort} onSort={toggle}>Anmeldung</Th>
                <Th k="activity" sort={sort} onSort={toggle}>Aktivität</Th>
                <Th k="errors" sort={sort} onSort={toggle} num>Fehler 7 T.</Th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((o) => (
                <tr key={o.id} className="click" onClick={() => onSelect(o.id)}>
                  <td>
                    <div className="pf-row">
                      <LogoPlate url={o.logoUrl} name={o.legalName} size={42} wide />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{o.legalName}</div>
                        {o.external && <div className="pf-muted" style={{ fontSize: 11.5 }}>Pilot-System · eingefroren, nur Ansicht</div>}
                        {o.internalNote && (
                          <div className="pf-muted" style={{ fontSize: 11.5, maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {o.internalNote}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{o.suspendedAt ? <Pill color={C.red}>Gesperrt</Pill> : <Pill color={C.green} dot>Aktiv</Pill>}</td>
                  <td className="num pf-mono">{nf(o.users)}</td>
                  <td className="num pf-mono">{nf(o.customers)}</td>
                  <td className="num pf-mono">{nf(o.invoices)}</td>
                  <td className="num pf-mono">{nf(o.articles)}</td>
                  <td style={{ color: "var(--pf-text-2)" }}>{ago(o.lastLoginAt)}</td>
                  <td style={{ color: "var(--pf-text-2)" }}>{ago(o.lastActivityAt)}</td>
                  <td className="num">{o.errors7 ? <Pill color={C.amber}>{o.errors7}</Pill> : <span className="pf-muted">—</span>}</td>
                </tr>
              ))}
              {!sorted.length && (
                <tr>
                  <td colSpan={9} className="pf-empty">{list.isLoading ? "Lädt …" : "Keine Betriebe gefunden."}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Trash />

      {creating && (
        <CreateOrg
          onClose={() => setCreating(false)}
          onCreated={(c, orgId) => {
            setCreating(false);
            setCreds({ title: "Betrieb angelegt", c });
            onSelect(orgId);
          }}
        />
      )}
      {selectedId && <OrgDetail id={selectedId} onClose={() => onSelect(null)} showCreds={(title, c) => setCreds({ title, c })} />}
      {/* zuletzt rendern: liegt dann über dem Detailfenster */}
      <CredentialsDialog creds={creds.c} title={creds.title} onClose={() => setCreds({ title: "", c: null })} />
    </div>
  );
}

function CreateOrg({ onClose, onCreated }: { onClose: () => void; onCreated: (c: NonNullable<Creds>, orgId: string) => void }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const [legalName, setLegalName] = useState("");
  const [legalForm, setLegalForm] = useState("GmbH");
  const [ownerLogin, setOwnerLogin] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const create = trpc.platform.orgs.create.useMutation({
    onSuccess: (r) => {
      utils.platform.orgs.list.invalidate();
      utils.platform.overview.invalidate();
      onCreated({ login: r.login, password: r.password, appUrl: r.appUrl }, r.orgId);
    },
    onError: (e) => toast({ kind: "err", title: "Anlegen fehlgeschlagen", text: e.message }),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title="Neuen Betrieb anlegen"
      lead="Grundeinstellungen, Nummernkreise, Standardtexte und der Inhaber-Zugang werden automatisch eingerichtet."
      width={520}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Abbrechen</Btn>
          <Btn
            variant="primary"
            loading={create.isPending}
            disabled={legalName.trim().length < 2 || ownerLogin.trim().length < 3}
            onClick={() => create.mutate({ legalName, legalForm, ownerLogin, ownerName: ownerName || undefined })}
          >
            Betrieb anlegen
          </Btn>
        </>
      }
    >
      <div className="pf-grid" style={{ gap: 14 }}>
        <Input label="Firmenname" value={legalName} onChange={(e) => setLegalName(e.target.value)} autoFocus />
        <Select
          label="Rechtsform"
          value={legalForm}
          onChange={(e) => setLegalForm(e.target.value)}
          options={["GmbH", "Einzelunternehmen", "OG", "KG", "e.U.", "AG", "Verein"].map((v) => ({ value: v, label: v }))}
        />
        <Input label="Anmeldename des Inhabers (E-Mail oder Benutzername)" value={ownerLogin} onChange={(e) => setOwnerLogin(e.target.value)} />
        <Input label="Name des Inhabers (optional)" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
        <div className="pf-muted" style={{ fontSize: 12 }}>Das Startpasswort wird automatisch erzeugt und danach einmalig angezeigt.</div>
      </div>
    </Modal>
  );
}

function OrgDetail({ id, onClose, showCreds }: { id: string; onClose: () => void; showCreds: (title: string, c: Creds) => void }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const q = trpc.platform.orgs.get.useQuery({ id });
  const [note, setNote] = useState<string | null>(null);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [supportOpen, setSupportOpen] = useState(false);
  const [newLogin, setNewLogin] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("office");
  const [resetFor, setResetFor] = useState<{ id: string; email: string } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editUser, setEditUser] = useState<{ id: string; name: string; login: string; origLogin: string } | null>(null);
  const [deleteName, setDeleteName] = useState("");

  const refresh = () => {
    utils.platform.orgs.get.invalidate({ id });
    utils.platform.orgs.list.invalidate();
    utils.platform.overview.invalidate();
  };
  const onError = (e: { message: string }) => toast({ kind: "err", title: "Fehler", text: e.message });

  const saveNote = trpc.platform.orgs.update.useMutation({ onSuccess: () => { refresh(); toast({ kind: "ok", title: "Notiz gespeichert" }); }, onError });
  const suspend = trpc.platform.orgs.suspend.useMutation({
    onSuccess: (r) => { setSuspendOpen(false); refresh(); toast({ kind: "ok", title: "Betrieb gesperrt", text: `${r.sessionsEnded} Sitzung(en) beendet` }); },
    onError,
  });
  const reactivate = trpc.platform.orgs.reactivate.useMutation({ onSuccess: () => { refresh(); toast({ kind: "ok", title: "Betrieb wieder aktiv" }); }, onError });
  const del = trpc.platform.orgs.delete.useMutation({
    onSuccess: () => {
      setDeleteOpen(false);
      utils.platform.orgs.list.invalidate();
      utils.platform.orgs.trash.invalidate();
      utils.platform.overview.invalidate();
      toast({ kind: "ok", title: "Betrieb im Papierkorb", text: "Du kannst ihn dort wiederherstellen oder endgültig löschen." });
      onClose();
    },
    onError,
  });
  const updateUser = trpc.platform.users.update.useMutation({
    onSuccess: () => {
      setEditUser(null);
      refresh();
      toast({ kind: "ok", title: "Benutzer gespeichert" });
    },
    onError,
  });
  const setFeature = trpc.platform.orgs.setFeature.useMutation({ onSuccess: refresh, onError });
  const setActive = trpc.platform.users.setActive.useMutation({ onSuccess: refresh, onError });
  const resetPw = trpc.platform.users.resetPassword.useMutation({ onSuccess: (r) => { setResetFor(null); showCreds("Neues Passwort", r); }, onError });
  const addUser = trpc.platform.users.add.useMutation({
    onSuccess: (r) => { setNewLogin(""); setNewName(""); refresh(); showCreds("Benutzer angelegt", r); },
    onError,
  });
  // Komplett neu laden: alle Abfragen müssen mit der neuen Sitzung (Kundenbetrieb) frisch geholt werden
  // Eigener Betrieb dieses Systems: neu laden (Sitzung wechselt). Pilot-Betrieb (anderes System): signierter Einmal-Link, in neuem Tab.
  const startSupport = trpc.platform.support.start.useMutation({
    onSuccess: (r) => {
      if (r.url) {
        setSupportOpen(false);
        const w = window.open(r.url, "_blank", "noopener");
        if (!w) window.location.assign(r.url);
      } else {
        window.location.href = "/";
      }
    },
    onError,
  });

  const d = q.data;
  const org = d?.org;
  const readOnly = !!org?.external;
  return (
    <Modal
      open
      onClose={onClose}
      width={1000}
      title={org?.legalName ?? "Betrieb"}
      lead={org ? `Kunde seit ${dt(org.createdAt)}` : undefined}
      logo={org ? <LogoPlate url={org.logoUrl} name={org.legalName} size={64} wide /> : undefined}
    >
      {q.isLoading || !d || !org ? (
        <p className="pf-muted">Lädt …</p>
      ) : (
        <div>
          {readOnly && (
            <div className="pf-status" style={{ marginBottom: 18, borderColor: "rgba(167,139,250,.45)", background: "linear-gradient(90deg, rgba(167,139,250,.14), rgba(167,139,250,.02) 60%)" }}>
              <div>
                <b>{org.externalLabel ?? "Pilot"}-System · nur Ansicht</b>
                <div className="pf-muted" style={{ marginTop: 2 }}>
                  Dieser Betrieb läuft auf dem eingefrorenen Pilot-System mit eigener Datenbank. Aus dem Admin Center lässt er sich ansehen und per Support-Zugang öffnen, aber nicht ändern, sperren oder löschen.
                </div>
              </div>
            </div>
          )}
          <div className="pf-row" style={{ flexWrap: "wrap" }}>
            {org.suspendedAt ? (
              <Pill color={C.red}>Gesperrt seit {dt(org.suspendedAt)}{org.suspendedReason ? ` · ${org.suspendedReason}` : ""}</Pill>
            ) : (
              <Pill color={C.green} dot>Aktiv</Pill>
            )}
            <div style={{ flex: 1 }} />
            <Btn onClick={() => setSupportOpen(true)}><LifeBuoy size={15} /> Als Betrieb öffnen (Support)</Btn>
            {!readOnly && <>
            {org.suspendedAt ? (
              <Btn variant="primary" loading={reactivate.isPending} onClick={() => reactivate.mutate({ id })}>Entsperren</Btn>
            ) : (
              <Btn variant="danger" onClick={() => setSuspendOpen(true)}>Zugang sperren</Btn>
            )}
            <Btn variant="danger" onClick={() => { setDeleteName(""); setDeleteOpen(true); }}><Trash2 size={15} /> Löschen</Btn>
            </>}
          </div>

          {!readOnly && <div className="pf-sec">
            <h3 className="pf-h">Interne Notiz · für den Kunden unsichtbar</h3>
            <Textarea rows={3} placeholder="Vertrag, Ansprechpartner, Besonderheiten …" value={note ?? org.internalNote ?? ""} onChange={(e) => setNote(e.target.value)} />
            <div style={{ marginTop: 10 }}>
              <Btn size="sm" disabled={note === null} loading={saveNote.isPending} onClick={() => saveNote.mutate({ id, internalNote: note ?? "" })}>Notiz speichern</Btn>
            </div>
          </div>}

          <div className="pf-sec">
            <h3 className="pf-h">Benutzer</h3>
            <div className="pf-panel pf-table-wrap" style={{ padding: 6 }}>
              <table className="pf-table">
                <thead><tr><th>Benutzer</th><th>Rolle</th><th>Letzte Anmeldung</th><th>Status</th>{!readOnly && <th />}</tr></thead>
                <tbody>
                  {d.users.map((u) => (
                    <tr key={u.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{u.name || u.email}</div>
                        {u.name && <div className="pf-muted" style={{ fontSize: 11.5 }}>{u.email}</div>}
                      </td>
                      <td>{ROLE_LABEL[u.role] ?? u.role}</td>
                      <td style={{ color: "var(--pf-text-2)" }}>{ago(u.lastLoginAt)}</td>
                      <td>{u.isActive ? <Pill color={C.green} dot>Aktiv</Pill> : <Pill color={C.red}>Gesperrt</Pill>}</td>
                      {!readOnly && <td>
                        <div className="pf-actions">
                        <Btn size="sm" variant="ghost" onClick={() => setEditUser({ id: u.id, name: u.name ?? "", login: u.email, origLogin: u.email })}><Pencil size={13} /> Bearbeiten</Btn>
                        <Btn size="sm" variant="ghost" onClick={() => setResetFor({ id: u.id, email: u.email })}>Passwort zurücksetzen</Btn>
                        <Btn size="sm" variant="ghost" onClick={() => setActive.mutate({ userId: u.id, active: !u.isActive })}>{u.isActive ? "Sperren" : "Aktivieren"}</Btn>
                        </div>
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!readOnly && <div className="pf-adduser">
              <Input label="Neuer Benutzer (Anmeldename)" value={newLogin} onChange={(e) => setNewLogin(e.target.value)} />
              <Input label="Name" value={newName} onChange={(e) => setNewName(e.target.value)} />
              <Select label="Rolle" value={newRole} onChange={(e) => setNewRole(e.target.value)} options={Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label }))} />
              <Btn loading={addUser.isPending} disabled={newLogin.trim().length < 3} onClick={() => addUser.mutate({ orgId: id, login: newLogin, name: newName || undefined, role: newRole as never })}>Hinzufügen</Btn>
            </div>}
          </div>

          {!readOnly && d.features.length > 0 && <div className="pf-sec">
            <h3 className="pf-h">Module</h3>
            {d.features.map((f) => (
              <div key={f.key} className="pf-panel pf-row" style={{ justifyContent: "space-between" }}>
                <span style={{ fontWeight: 600 }}>{f.label}</span>
                <Switch checked={f.enabled} label={f.label} onChange={(v) => setFeature.mutate({ id, feature: f.key, enabled: v })} />
              </div>
            ))}
          </div>}

          <div className="pf-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", marginTop: 22, gap: 22 }}>
            <section>
              <h3 className="pf-h">Letzte Fehler</h3>
              {d.errors.length ? (
                <ul className="pf-list">
                  {d.errors.map((e) => (
                    <li key={e.id}>
                      <span className="pf-muted pf-mono" style={{ fontSize: 11 }}>{dt(e.at)} · </span>
                      <code>{e.path}</code>
                      <div style={{ color: "var(--pf-text-2)" }}>{e.message}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="pf-row pf-muted"><Dot /> Keine Fehler.</div>
              )}
              {!!d.jobs.length && (
                <>
                  <h3 className="pf-h" style={{ marginTop: 22 }}>Katalog-Updates</h3>
                  <ul className="pf-list">
                    {d.jobs.map((j) => (
                      <li key={j.id}>
                        <span className="pf-muted pf-mono" style={{ fontSize: 11 }}>{dt(j.createdAt)} · </span>
                        {j.status}
                        {j.error && <span style={{ color: C.red }}> — {j.error}</span>}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
            <section>
              <h3 className="pf-h">Letzte Aktivität</h3>
              {d.activity.length ? (
                <ul className="pf-list">
                  {d.activity.map((a) => (
                    <li key={a.id}>
                      <span className="pf-muted pf-mono" style={{ fontSize: 11 }}>{dt(a.at)} · </span>
                      {a.who} <span className="pf-muted">— {a.what}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="pf-muted">Noch keine Aktivität.</div>
              )}
            </section>
          </div>
        </div>
      )}

      <Modal
        open={!!editUser}
        onClose={() => setEditUser(null)}
        title="Benutzer bearbeiten"
        lead="Der Name erscheint als SachbearbeiterIn in neuen Angeboten, Rechnungen und PDFs. Bereits ausgestellte Dokumente behalten den Namen, der beim Erstellen gespeichert wurde."
        width={500}
        footer={
          <>
            <Btn variant="ghost" onClick={() => setEditUser(null)}>Abbrechen</Btn>
            <Btn
              variant="primary"
              loading={updateUser.isPending}
              disabled={!editUser || editUser.name.trim().length < 1 || editUser.login.trim().length < 3}
              onClick={() =>
                editUser &&
                updateUser.mutate({
                  userId: editUser.id,
                  name: editUser.name,
                  login: editUser.login.trim().toLowerCase() !== editUser.origLogin.toLowerCase() ? editUser.login : undefined,
                })
              }
            >
              Speichern
            </Btn>
          </>
        }
      >
        {editUser && (
          <div className="pf-grid" style={{ gap: 14 }}>
            <Input label="Name (SachbearbeiterIn)" value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} autoFocus />
            <Input label="Anmeldename (E-Mail oder Benutzername)" value={editUser.login} onChange={(e) => setEditUser({ ...editUser, login: e.target.value })} />
            <div className="pf-muted" style={{ fontSize: 12 }}>Ändert sich der Anmeldename, muss sich der Benutzer künftig damit anmelden. Das Passwort bleibt gleich.</div>
          </div>
        )}
      </Modal>
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Betrieb löschen?"
        lead="Der Betrieb kommt in den Papierkorb: Alle Benutzer werden sofort abgemeldet und er verschwindet aus allen Listen. Die Daten bleiben erhalten, bis du ihn im Papierkorb endgültig löschst — bis dahin kannst du ihn wiederherstellen."
        width={520}
        footer={
          <>
            <Btn variant="ghost" onClick={() => setDeleteOpen(false)}>Abbrechen</Btn>
            <Btn
              variant="danger"
              loading={del.isPending}
              disabled={deleteName.trim().toLowerCase() !== (org?.legalName ?? "\u0000").trim().toLowerCase()}
              onClick={() => del.mutate({ id, confirmName: deleteName })}
            >
              In den Papierkorb
            </Btn>
          </>
        }
      >
        <Input
          label={`Zur Bestätigung den Namen eintippen: ${org?.legalName ?? ""}`}
          value={deleteName}
          onChange={(e) => setDeleteName(e.target.value)}
          autoFocus
        />
      </Modal>
      <Modal
        open={suspendOpen}
        onClose={() => setSuspendOpen(false)}
        title="Zugang sperren?"
        lead="Alle Benutzer dieses Betriebs werden sofort abgemeldet und können sich nicht mehr anmelden. Die Daten bleiben vollständig erhalten."
        width={480}
        footer={
          <>
            <Btn variant="ghost" onClick={() => setSuspendOpen(false)}>Abbrechen</Btn>
            <Btn variant="danger" loading={suspend.isPending} onClick={() => suspend.mutate({ id, reason: reason || undefined })}>Jetzt sperren</Btn>
          </>
        }
      >
        <Input label="Grund (intern, optional)" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z. B. Zahlungsverzug" />
      </Modal>
      <Confirm
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        title="Support-Zugang starten?"
        message={readOnly ? `Das Pilot-System öffnet sich in einem neuen Tab, und du wirst dort als Inhaber von „${org?.legalName ?? ""}" angemeldet (2 Stunden, roter Support-Hinweis oben). Der Zugang wird im Aktivitätsprotokoll des Betriebs vermerkt. Zum Beenden „Support beenden" klicken oder den Tab schließen.` : `Du wirst als Inhaber von „${org?.legalName ?? ""}" angemeldet (2 Stunden). Der Zugang wird im Aktivitätsprotokoll des Betriebs vermerkt. Mit „Support beenden" kommst du zurück.`}
        confirmLabel="Als Betrieb öffnen"
        loading={startSupport.isPending}
        onConfirm={() => startSupport.mutate({ orgId: id })}
      />
      <Confirm
        open={!!resetFor}
        onClose={() => setResetFor(null)}
        title="Passwort zurücksetzen?"
        message={`Für „${resetFor?.email ?? ""}" wird ein neues Zufallspasswort gesetzt. Alle laufenden Sitzungen dieses Benutzers werden beendet.`}
        confirmLabel="Zurücksetzen"
        loading={resetPw.isPending}
        onConfirm={() => resetFor && resetPw.mutate({ userId: resetFor.id })}
      />
    </Modal>
  );
}
